import React from 'react';
import ReportCard from '../components/ReportCard';
import { bucketCount, fmtCount, fmtPct, share, tip } from '../format';

const CoverageCard = ({ value, valueClassName, title, caption, borderClassName, tooltip }) => (
  <div
    data-tip={tooltip}
    className={`rounded-ds-lg border bg-white p-[22px] ${borderClassName}`}
  >
    <div
      className={`font-jetbrains-mono text-[36px] font-semibold leading-none tabular-nums ${valueClassName}`}
    >
      {value}
    </div>
    <div className='mt-ds-3 text-[13px] font-semibold text-gray-900'>{title}</div>
    <div className='mt-[5px] text-[12px] text-gray-600'>{caption}</div>
  </div>
);

/**
 * The honesty section: how much of the cohort was actually measured for the two markers that are not
 * part of a standard blood panel, and what that does to the headline number.
 *
 * The projected range is derived — the syndrome rate observed among employees who *were* measured,
 * applied to the whole cohort — rather than a figure typed into the design.
 */
const CoverageCaveat = ({ data }) => {
  const total = data.cohort.total;
  const coverage = data.coverage || {};
  const bmi = coverage.bmi || {};
  const bp = coverage.bp || {};
  const observed = share(bucketCount(data.buckets, 3), total);
  const bmiAbnormalRate = share(bmi.abnormal, bmi.measured);

  // If the untested majority carried abnormalities at the rate seen in the tested sub-sample, the
  // syndrome share would land between the observed floor and this ceiling.
  const projected = Math.min(100, observed + bmiAbnormalRate * (1 - share(bmi.measured, total) / 100));

  return (
    <ReportCard
      id='caveat'
      fileName='coverage-caveat'
      surface='rounded-ds-xl border border-ds-amber-200 border-l-4 border-l-ds-amber bg-ds-amber-lt'
      className='mt-ds-9 scroll-mt-[130px] px-[34px] py-ds-7'
    >
      <div className='text-ts-2xs font-semibold uppercase tracking-[0.12em] text-ds-amber-dk'>
        A caveat worth acting on
      </div>
      <h3
        className='mb-ds-2 mt-ds-3 max-w-[760px] font-fig-extrabold text-[32px] font-bold leading-[1.15] tracking-[-0.02em] text-gray-900'
        style={{ textWrap: 'pretty' }}
      >
        {`The ${fmtPct(observed)} figure is likely a floor, not the full picture`}
      </h3>

      <div className='mt-[26px] grid grid-cols-1 gap-ds-4 md:grid-cols-3'>
        <CoverageCard
          value={fmtPct(share(bmi.measured, total))}
          valueClassName='text-ds-amber-dk'
          title='of the cohort was measured for BMI'
          caption={
            <>
              <span className='font-jetbrains-mono font-semibold'>{fmtCount(bmi.measured)}</span>
              {` of ${fmtCount(total)} employees`}
            </>
          }
          borderClassName='border-ds-amber-200'
          tooltip={tip(`${fmtCount(bmi.measured)} of ${fmtCount(total)} employees were BMI-measured`)}
        />
        <CoverageCard
          value={fmtPct(share(bp.checked, total))}
          valueClassName='text-ds-amber-dk'
          title='of the cohort was checked for BP'
          caption={
            <>
              <span className='font-jetbrains-mono font-semibold'>{fmtCount(bp.checked)}</span>
              {` of ${fmtCount(total)} employees`}
              {bp.cuff_measured === 0 && bp.self_reported > 0
                ? '; self-reported “treated for BP”, not a cuff reading'
                : `; ${fmtCount(bp.cuff_measured)} from a cuff reading`}
            </>
          }
          borderClassName='border-ds-amber-200'
          tooltip={tip(
            `${fmtCount(bp.checked)} of ${fmtCount(total)} employees`,
            `${fmtCount(bp.cuff_measured)} cuff · ${fmtCount(bp.self_reported)} self-reported`
          )}
        />
        <CoverageCard
          value={fmtPct(bmiAbnormalRate)}
          valueClassName='text-ds-rose-dk'
          title={
            <>
              {'of those who '}
              <em className='not-italic underline underline-offset-[3px]'>were</em>
              {' BMI-measured were overweight / obese'}
            </>
          }
          caption='The true BMI picture is likely far worse org-wide'
          borderClassName='border-ds-rose-200'
          tooltip={tip(`Of the ${fmtCount(bmi.measured)} measured, ${fmtPct(bmiAbnormalRate)} were overweight or obese`)}
        />
      </div>

      <p
        className='m-0 mt-[26px] max-w-[900px] text-[17px] leading-[1.7] text-gray-800'
        style={{ textWrap: 'pretty' }}
      >
        <strong className='font-semibold text-gray-900'>Net effect:</strong>
        {' if BMI and BP were screened across the full workforce at the rates seen in the tested sub-samples, '}
        the true share of employees meeting metabolic syndrome criteria would plausibly land in the{' '}
        <strong className='font-jetbrains-mono font-semibold text-ds-rose-dk'>
          {`${fmtPct(observed, 0)}–${fmtPct(projected, 0)}`}
        </strong>{' '}
        {`range — above the ${fmtPct(observed)} visible in current records.`}
      </p>
    </ReportCard>
  );
};

export default CoverageCaveat;
